/**
 * ============================================
 * ANALYTICS DASHBOARD
 * ============================================
 * 
 * Game analytics dashboard showing:
 * - Total players & regions
 * - Geographical distribution
 * - Session length & retention
 * - Level progress & statistics (SinglePlayer, Labz, All Levels)
 * - Global time distribution
 * - Editor usage metrics
 * - Onboarding funnel
 * - Player churn analysis
 * - Cinematic & Feedback stats
 */

import { useState, useRef, useCallback, useMemo, type ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import {
  Clock, TrendingUp,
  RefreshCw, Layers, Palette, Bot, Timer,
  ChevronDown, Download, Film, MessageSquare,
  ChevronUp, Database, Settings, Gamepad2, FlaskConical, BarChart3,
  AlertTriangle, Users, Blocks, Trophy,
  Coins, MousePointerClick, Sparkles, Square, Swords, Calendar, Globe
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Legend, ReferenceLine
} from 'recharts';
import { useAnalytics, type AnalyticsData } from '@/hooks/useAnalytics';
import { useTheme } from '@/context/ThemeContext';
import { useLevelSettings } from '@/context/LevelSettingsContext';
import { useDateRange } from '@/context/DateRangeContext';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { deriveRangeData, withLocalModeTimes } from '@/utils/rangeData';
import { MARKER_CHART_MARGIN, markerLabel } from '@/components/common/ChartMarkers';
import { formatAxisCount } from '@/lib/utils';
import { CountryMultiSelect } from '@/components/common/CountryMultiSelect';
import { useAnalyticsCountries } from '@/hooks/useAnalyticsCountries';
import { applyCountryFilter, countryOptions, editorTopCountries, type AnalyticsCountryPayload } from '@/utils/analyticsCountryFilter';
import { CountryChips } from '@/components/common/CountryChips';
import { CardCountryButton, CountryCard, CountryScope, useCardOverrides, useViewCache } from '@/components/common/CardCountryFilter';
import { EditorTopCountries } from './EditorTopCountries';
import { LevelSettingsModal } from './LevelSettingsModal';
import { LevelSearch } from './LevelSearch';

// ============================================
// CONSTANTS
// ============================================

// Default level categorizations based on known level patterns
const SINGLEPLAYER_PATTERNS = [
  'jungle', 'nightcity', 'lava', 'monastery', 'monastry',
  'gravgun', 'supertraversal', 'super_traversal', 'supertraverasl',
  'icebergs',
  'desert eagle', 'desert_eagle', 'deserteagle',
];
const LABZ_PATTERNS = ['superpunch', 'heaven', 'cubegun', 'hell', '2dplatformer', 'rollerball'];

// Levels that should be Single Player even when they carry a `labs_` prefix
// (e.g. LABS_SUPERTRAVERSAL → Icebergs is a Single Player level).
const SINGLEPLAYER_OVERRIDE_PATTERNS = [
  'supertraversal', 'super_traversal', 'supertraverasl', 'icebergs',
  'desert eagle', 'desert_eagle', 'deserteagle',
];

// Display name mappings for level IDs
// Display name mappings — checked longest pattern first to avoid partial matches
const LEVEL_DISPLAY_NAMES: [string, string][] = [
  ['jungle runner', 'Jungle Hunger'],
  ['jungle_runner', 'Jungle Hunger'],
  ['labs_supertraversal', 'Icebergs'],
  ['super_traversal', 'Icebergs'],
  ['supertraversal', 'Icebergs'],
  ['supertraverasl', 'Icebergs'],  // common typo in raw data
  ['icebergs', 'Icebergs'],
  ['desert eagle', 'Desert Eagle'],
  ['desert_eagle', 'Desert Eagle'],
  ['deserteagle', 'Desert Eagle'],
  ['gravgun', 'Desert'],
  ['cubegun', 'Floor is Lava'],
];

// Preferred display order for the Single Player chart (lower index = first).
// Matches against the *display name* (lowercased). Levels not listed go last
// in alphabetical order.
const SINGLEPLAYER_DISPLAY_ORDER: string[] = [
  'jungle',
  'nightcity',
  'lava',
  'monastery',
  'monastry',
  'icebergs',
  'desert',          // matches "Desert" (must come BEFORE "desert eagle" check, but
                     // we sort by exact display name so order in this list is what
                     // matters — Desert is index 5, Desert Eagle is index 6).
  'desert eagle',
  'jungle hunger',
  'return to jungle',
];

function singlePlayerSortIndex(levelId: string): number {
  const display = getLevelDisplayName(levelId).toLowerCase();
  // Exact display-name match first (so "desert" doesn't accidentally match "desert eagle")
  const exact = SINGLEPLAYER_DISPLAY_ORDER.indexOf(display);
  if (exact !== -1) return exact;
  // Fall back to substring match against the raw lowercased ID
  const lowerId = levelId.toLowerCase();
  for (let i = 0; i < SINGLEPLAYER_DISPLAY_ORDER.length; i++) {
    if (lowerId.includes(SINGLEPLAYER_DISPLAY_ORDER[i])) return i;
  }
  return Number.MAX_SAFE_INTEGER;
}

function getLevelDisplayName(levelId: string): string {
  const lower = levelId.toLowerCase();
  for (const [pattern, name] of LEVEL_DISPLAY_NAMES) {
    if (lower.includes(pattern)) return name;
  }
  return levelId;
}

// BlockType enum names matching the C# enum order (index = numeric ID from game)
const BLOCK_TYPE_NAMES: string[] = [
  'CubeBlue', 'CubeOrange', 'Switch', 'Accelerator', 'Debug',
  'SpikedCube', 'SpikedWheel', 'SpikedWheelFlat',
  'BushApple', 'BushGreen', 'BushRed', 'TreeApple', 'TreeGreen', 'TreeRed',
  'Platform3X3', 'Platform5X5', 'Platform9X9',
  'SwitchPushPlate', 'SwitchProximity', 'Delay', 'EndPortal', 'CheckPoint',
  'SpikedSphere', 'Pendulum', 'LaserEmitter',
  'PlatformSingleSpike', 'PlatformDoubleSpike', 'Door',
  'NpcRunner', 'NpcHopper', 'NpcSpider', 'NpcChef', 'NpcBomber', 'NpcFly',
  'NpcLaserFrog2', 'NpcLaserFrog4',
  'SuperPunch', 'CubeLauncher', 'PetEmitter', 'Earthquake',
  'GlideBoost', 'Magnet', 'Spin', 'TimeSlowDown',
  'Coin', 'Star', 'Orb',
  'PlatformVertical3X3', 'Roll',
  'CubeYellow', 'CubeGreen', 'CubePurple', 'LECubeGun', 'HapticCube',
  'SnakeBoss', 'SpiderBoss', 'EmojiBubble',
  'ArchTall', 'ArchShort', 'PlatformCurve', 'Tower', 'PlatformCircular',
  'Ramp', 'PlatformRotating', 'PlatformSpikedRotating',
  'PlatformMovingHorizontal', 'PlatformMovingVertical',
  'CylinderBlockyRotating', 'CylinderBlockySpikedRotating',
  'CylinderCurvedRotating', 'CylinderCurvedSpikedRotating',
  'LEMoveGun', 'LaserRingStomper', 'RotatingSpikedBallLauncher', 'StartPortal',
  'PushObstacleSideToSide', 'PushObstacleRotating',
  'SpiralRamp', 'Stairs3x3', 'Bridge', 'Octagon',
  'DirectionIndicatorWall', 'JumpPad', 'BigRamp', 'HorizontalBooster',
  'SpeechBubble', 'CubeRed', 'CubeWhite', 'CubeGlass',
];

// BlockType2D enum names matching the C# enum order
const BLOCK_TYPE_2D_NAMES: string[] = [
  'Blue2dCube', 'Orange2dCube', 'StartPortal', 'Green2dCube',
  'SpikePendulum', 'Checkpoint', 'Enemy', 'SpikeCube',
  'SpikeWheel', 'EndPortal', 'HapticCube',
];

/** Convert a numeric block ID to its BlockType enum name */
function resolveBlockName(blockId: string, is2D: boolean = false): string {
  const num = Number(blockId);
  const names = is2D ? BLOCK_TYPE_2D_NAMES : BLOCK_TYPE_NAMES;
  if (!isNaN(num) && Number.isInteger(num) && num >= 0 && num < names.length) {
    return names[num];
  }
  return blockId;
}

// Helper to get default category
function getDefaultLevelCategory(levelId: string): 'singleplayer' | 'labz' | null {
  const lower = levelId.toLowerCase();
  // Single Player overrides — these levels are SP even when carrying a labs_
  // prefix (e.g. LABS_SUPERTRAVERSAL → Icebergs).
  for (const pattern of SINGLEPLAYER_OVERRIDE_PATTERNS) {
    if (lower.includes(pattern)) return 'singleplayer';
  }
  // Priority: anything prefixed with labs_/labz_ belongs to Labs, regardless
  // of sub-patterns (e.g. LABS_2D_JUNGLE_1 must not fall into Single Player
  // just because it contains "jungle").
  if (lower.startsWith('labs_') || lower.startsWith('labs ') || lower.startsWith('labz_') || lower.startsWith('labz ')) {
    return 'labz';
  }
  for (const pattern of SINGLEPLAYER_PATTERNS) {
    if (lower.includes(pattern)) return 'singleplayer';
  }
  for (const pattern of LABZ_PATTERNS) {
    if (lower.includes(pattern)) return 'labz';
  }
  return null;
}

// ============================================
// HELPERS
// ============================================

function formatCount(count: number): string {
  if (count >= 1000000) {
    const value = count / 1000000;
    return value % 1 === 0 ? `${value}M` : `${value.toFixed(1)}M`;
  }
  if (count >= 1000) {
    const value = count / 1000;
    return value % 1 === 0 ? `${value}K` : `${value.toFixed(1)}K`;
  }
  return count.toString();
}

// Whole days elapsed between an editor's data-collection start date (ISO
// YYYY-MM-DD) and today. Used to show "Since N days" on the editor cards so the
// count auto-increments daily instead of being a fixed date/number.
function daysSince(isoDate: string): number {
  const start = new Date(`${isoDate}T00:00:00`);
  const diffMs = Date.now() - start.getTime();
  return Math.max(0, Math.floor(diffMs / 86400000));
}

// ============================================
// ============================================
// EDITOR SESSIONS CHART COMPONENT
// ============================================

const EDITOR_SERIES = [
  { key: '3D Editor',    color: '#eab308' }, // yellow
  { key: '2D Editor',    color: '#3b82f6' }, // blue
  { key: 'Avatar',       color: '#a855f7' }, // purple
  { key: 'NPC',          color: '#22c55e' }, // green
  { key: 'Track Editor', color: '#f59e0b' }, // amber
  { key: 'Chassis',      color: '#06b6d4' }, // cyan
  { key: 'Coop',         color: '#ec4899' }, // pink
] as const;

type EditorKey = typeof EDITOR_SERIES[number]['key'];
const ALL_EDITORS_VISIBLE: Record<EditorKey, boolean> = {
  '3D Editor': true, '2D Editor': true, 'Avatar': true, 'NPC': true, 'Track Editor': true,
  'Chassis': true, 'Coop': true,
};

function EditorSessionsChart({ data, editorTimeScale, setEditorTimeScale, isDarkMode, gridColor, textColor, tooltipStyle }: {
  data: any; editorTimeScale: 'daily' | 'weekly' | 'monthly'; setEditorTimeScale: (s: 'daily' | 'weekly' | 'monthly') => void;
  isDarkMode: boolean; gridColor: string; textColor: string; tooltipStyle: any;
}) {
  const [visibleEditors, setVisibleEditors] = useState<Record<EditorKey, boolean>>(ALL_EDITORS_VISIBLE);
  const allVisible = EDITOR_SERIES.every(e => visibleEditors[e.key]);
  const { inRange } = useDateRange();

  const raw = data?.dailyEditorOpens as Record<string, Record<string, number>> | undefined;
  const hasData = raw && Object.keys(raw).length > 0;

  const sortedDates = hasData ? Object.keys(raw!).filter(inRange).sort() : [];

  let chartData: Array<Record<string, any>>;
  if (!hasData) {
    chartData = [];
  } else if (editorTimeScale === 'daily') {
    chartData = sortedDates.map(d => {
      const day = raw![d];
      const dt = new Date(d);
      return {
        date: `${dt.getDate().toString().padStart(2, '0')}-${dt.toLocaleDateString('en-US', { month: 'short' })}`,
        '3D Editor': day.level_editor || 0, '2D Editor': day.editor_2d || 0,
        'Avatar': day.avatar_editor || 0, 'NPC': day.npc_editor || 0, 'Track Editor': day.track_editor || 0,
        'Chassis': day.chassis_editor || 0, 'Coop': day.coop_level_editor || 0,
      };
    });
  } else {
    const groups: Record<string, { label: string; le: number; e2d: number; av: number; npc: number; te: number; ch: number; co: number }> = {};
    sortedDates.forEach(d => {
      const dt = new Date(d);
      const day = raw![d];
      let key: string, label: string;
      if (editorTimeScale === 'weekly') {
        const ws = new Date(dt); ws.setDate(dt.getDate() - dt.getDay());
        key = ws.toISOString().split('T')[0];
        label = `${ws.getDate().toString().padStart(2,'0')}-${ws.toLocaleDateString('en-US',{month:'short'})}`;
      } else {
        key = `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}`;
        label = dt.toLocaleDateString('en-US',{month:'short',year:'2-digit'});
      }
      if (!groups[key]) groups[key] = { label, le: 0, e2d: 0, av: 0, npc: 0, te: 0, ch: 0, co: 0 };
      groups[key].le += day.level_editor || 0;
      groups[key].e2d += day.editor_2d || 0;
      groups[key].av += day.avatar_editor || 0;
      groups[key].npc += day.npc_editor || 0;
      groups[key].te += day.track_editor || 0;
      groups[key].ch += day.chassis_editor || 0;
      groups[key].co += day.coop_level_editor || 0;
    });
    chartData = Object.entries(groups).sort(([a],[b]) => a.localeCompare(b)).map(([,g]) => ({
      date: g.label, '3D Editor': g.le, '2D Editor': g.e2d, 'Avatar': g.av, 'NPC': g.npc, 'Track Editor': g.te,
      'Chassis': g.ch, 'Coop': g.co,
    }));
  }

  const toggleEditor = (key: EditorKey) => {
    setVisibleEditors(prev => ({ ...prev, [key]: !prev[key] }));
  };
  const resetEditors = () => setVisibleEditors(ALL_EDITORS_VISIBLE);

  return (
    <ThemedCard isDark={isDarkMode}>
      <div className="p-4 border-b border-gray-700/50 flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Timer className="h-4 w-4" />
          Editor Sessions
        </h3>
        <CardCountryButton />
        <div className="flex gap-1">
          {(['daily', 'weekly', 'monthly'] as const).map((s) => (
            <button key={s} onClick={() => setEditorTimeScale(s)}
              className={`px-2 py-1 text-xs rounded ${editorTimeScale === s
                ? (isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                : (isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300')
              }`}>{s.charAt(0).toUpperCase() + s.slice(1)}</button>
          ))}
        </div>
      </div>
      {/* Editor visibility toggles */}
      <div className={`px-4 py-2 border-b ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200'} flex items-center gap-3 flex-wrap`}>
        <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Show:</span>
        {EDITOR_SERIES.map(({ key, color }) => (
          <label key={key} className={`flex items-center gap-1.5 text-xs cursor-pointer select-none ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <input
              type="checkbox"
              checked={visibleEditors[key]}
              onChange={() => toggleEditor(key)}
              className="h-3.5 w-3.5 cursor-pointer accent-blue-500"
            />
            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
            {key}
          </label>
        ))}
        <button
          onClick={resetEditors}
          disabled={allVisible}
          className={`ml-auto px-2 py-0.5 text-xs rounded transition-colors ${
            allVisible
              ? (isDarkMode ? 'bg-gray-800 text-gray-600 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed')
              : (isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300')
          }`}
          title="Show all editors"
        >
          Reset
        </button>
      </div>
      <div className="p-4 h-[300px]">
        {chartData.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Awaiting next analytics sync for daily editor data...
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis dataKey="date" tick={({ x, y, payload }: any) => (<text x={x} y={y} dy={10} textAnchor="end" fill={textColor} fontSize={9} transform={`rotate(-35, ${x}, ${y})`}>{String(payload.value).toUpperCase()}</text>)} stroke={gridColor} height={60} />
              <YAxis tick={{ fontSize: 10, fill: textColor }} stroke={gridColor} tickFormatter={formatAxisCount} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
              {EDITOR_SERIES.filter(e => visibleEditors[e.key]).map(e => (
                <Line key={e.key} type="monotone" dataKey={e.key} stroke={e.color} strokeWidth={2} dot={{ r: 2 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </ThemedCard>
  );
}

// Friend Requests — stacked bar chart. Each bar = Sent + Accepted + Rejected.
// A header checkbox combines Accepted + Rejected into a single "Interacted" part.
// Sourced from friend_request_sent (per-day count) and friend_request_interaction
// (per-day, bucketed by its `state` param). Self-contained (own time-scale +
// combine state), modelled on EditorSessionsChart.
function FriendRequestsChart({ data, isDarkMode, gridColor, textColor, tooltipStyle }: {
  data: any; isDarkMode: boolean; gridColor: string; textColor: string; tooltipStyle: any;
}) {
  const [timeScale, setTimeScale] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [combine, setCombine] = useState(false);
  const { inRange } = useDateRange();

  const sent = (data?.dailyFriendRequestSent || {}) as Record<string, number>;
  const inter = (data?.dailyFriendRequestInteraction || {}) as Record<string, Record<string, number>>;
  const unfriended = (data?.dailyFriendUnfriended || {}) as Record<string, number>;
  const sortedDates = Array.from(new Set([...Object.keys(sent), ...Object.keys(inter), ...Object.keys(unfriended)])).filter(inRange).sort();

  const at = (d: string) => ({
    sent: sent[d] || 0,
    accepted: inter[d]?.['accepted'] || 0,
    rejected: inter[d]?.['rejected'] || 0,
    unfriended: unfriended[d] || 0,
  });

  let chartData: Array<Record<string, any>> = [];
  if (sortedDates.length > 0 && timeScale === 'daily') {
    chartData = sortedDates.map(d => {
      const v = at(d); const dt = new Date(d);
      return {
        date: `${dt.getDate().toString().padStart(2, '0')}-${dt.toLocaleDateString('en-US', { month: 'short' })}`,
        sent: v.sent, accepted: v.accepted, rejected: v.rejected, interacted: v.accepted + v.rejected, unfriended: v.unfriended,
      };
    });
  } else if (sortedDates.length > 0) {
    const groups: Record<string, { label: string; sent: number; accepted: number; rejected: number; unfriended: number }> = {};
    sortedDates.forEach(d => {
      const v = at(d); const dt = new Date(d);
      let key: string, label: string;
      if (timeScale === 'weekly') {
        const ws = new Date(dt); ws.setDate(dt.getDate() - dt.getDay());
        key = ws.toISOString().split('T')[0];
        label = `${ws.getDate().toString().padStart(2, '0')}-${ws.toLocaleDateString('en-US', { month: 'short' })}`;
      } else {
        key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
        label = dt.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      }
      if (!groups[key]) groups[key] = { label, sent: 0, accepted: 0, rejected: 0, unfriended: 0 };
      groups[key].sent += v.sent; groups[key].accepted += v.accepted; groups[key].rejected += v.rejected; groups[key].unfriended += v.unfriended;
    });
    chartData = Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)).map(([, g]) => ({
      date: g.label, sent: g.sent, accepted: g.accepted, rejected: g.rejected, interacted: g.accepted + g.rejected, unfriended: g.unfriended,
    }));
  }

  return (
    <ThemedCard isDark={isDarkMode}>
      <div className="p-4 border-b border-gray-700/50 flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Users className="h-4 w-4" />
          Friend Requests
        </h3>
        <CardCountryButton />
        <div className="flex gap-1">
          {(['daily', 'weekly', 'monthly'] as const).map((s) => (
            <button key={s} onClick={() => setTimeScale(s)}
              className={`px-2 py-1 text-xs rounded ${timeScale === s
                ? (isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                : (isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300')
              }`}>{s.charAt(0).toUpperCase() + s.slice(1)}</button>
          ))}
        </div>
      </div>
      <div className={`px-4 py-2 border-b ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200'} flex items-center gap-3 flex-wrap`}>
        <label className={`flex items-center gap-1.5 text-xs cursor-pointer select-none ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          <input type="checkbox" checked={combine} onChange={() => setCombine(v => !v)}
            className="h-3.5 w-3.5 cursor-pointer accent-blue-500" />
          Combine Accepted + Rejected into "Interacted"
        </label>
      </div>
      <div className="p-4 h-[300px]">
        {chartData.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Awaiting next analytics sync for friend-request data...
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis dataKey="date" tick={({ x, y, payload }: any) => (<text x={x} y={y} dy={10} textAnchor="end" fill={textColor} fontSize={9} transform={`rotate(-35, ${x}, ${y})`}>{String(payload.value).toUpperCase()}</text>)} stroke={gridColor} height={60} />
              <YAxis tick={{ fontSize: 10, fill: textColor }} stroke={gridColor} tickFormatter={formatAxisCount} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
              <Bar dataKey="sent" stackId="fr" name="Sent" fill="#3b82f6" />
              {combine ? (
                <Bar dataKey="interacted" stackId="fr" name="Interacted (Accepted + Rejected)" fill="#a855f7" />
              ) : (
                <>
                  <Bar dataKey="accepted" stackId="fr" name="Accepted" fill="#22c55e" />
                  <Bar dataKey="rejected" stackId="fr" name="Rejected" fill="#ef4444" />
                </>
              )}
              <Bar dataKey="unfriended" stackId="fr" name="Unfriended" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </ThemedCard>
  );
}

// ============================================
// STAT CARD COMPONENT (for small stats)
// ============================================

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subtitle?: string;
  isDark?: boolean;
}

function StatCard({ icon, label, value, subtitle, isDark = true }: StatCardProps) {
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-gray-100 border border-gray-200'}`}>
      <div className="flex items-center gap-2">
        {icon}
        <span className={`text-sm ${isDark ? '' : 'text-gray-700'}`}>{label}</span>
      </div>
      <div className="text-right">
        <span className={`font-bold ${isDark ? '' : 'text-gray-900'}`}>{value}</span>
        {subtitle && <span className={`text-xs ml-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{subtitle}</span>}
      </div>
    </div>
  );
}

// ============================================
// THEMED CARD COMPONENT
// ============================================

interface ThemedCardProps {
  children: React.ReactNode;
  isDark?: boolean;
  className?: string;
}

function ThemedCard({ children, isDark = true, className = '' }: ThemedCardProps) {
  return (
    <div className={`rounded-xl border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200 shadow-md'} ${className}`}>
      {children}
    </div>
  );
}

// ============================================
// MAIN DASHBOARD COMPONENT
// ============================================

// Local PvP levels never send level_completed: a round ends on the winner screen
// (4_Pvp/LevelCompleted.cs), not by touching a level exit, so PvP completion is always 0%.
// Hidden until the game sends a completion event for PvP. Flip to true to bring it back.
const SHOW_PVP_COMPLETION = false;

type EconomyRow = { level: string; coin: number; star: number; orb: number };

/**
 * Everything the Analytics cards render, for ONE country selection: the
 * country-filtered snapshot, the date-range view of it, and the per-category
 * level / economy / menu / multiplier splits. The tab view and every card that
 * overrides the tab's countries call this (cached per selection).
 */
function buildAnalyticsView({ rawData, payload, ready, selection, inRange, isAllTime, getLevelCategoryWithDefault }: {
  rawData: AnalyticsData | null;
  payload: AnalyticsCountryPayload | null;
  ready: boolean;
  selection: string[];
  inRange: (d?: string | null) => boolean;
  isAllTime: boolean;
  getLevelCategoryWithDefault: (levelId: string) => string;
}) {
  const countryActive = selection.length > 0 && ready;
  const data = rawData && countryActive && payload ? applyCountryFilter(rawData, payload, selection) : rawData;
  const editorTop = ready && payload ? editorTopCountries(payload, selection, d => inRange(d)) : null;
  // A country selection always re-derives from the partitions (all days when the
  // range is All Time), so attempt-based level stats replace the all-time
  // unique-player ones, same as when a date range is set.
  const rangedData = deriveRangeData(data, inRange, isAllTime && !countryActive);
  const rv = rangedData ?? data;
  const D = withLocalModeTimes(rv, data, inRange);

  // Filtered data for SinglePlayer levels
  const singlePlayerData = (() => {
    if (!rv?.campaignProgress || !rv?.levelStats) return { campaign: [], stats: [] };

    // entries-by-raw-level lookup, used to weight when merging duplicate
    // display names (e.g. SuperTraversal + SuperTraverasl + Icebergs all
    // collapse into one "Icebergs" bar).
    const entriesByLevel: Record<string, number> = {};
    for (const s of rv.levelStats) entriesByLevel[s.level] = s.entries || 0;

    // ---- Merge campaignProgress by display name ----
    const campaignFiltered = rv.campaignProgress.filter(c =>
      getLevelCategoryWithDefault(c.level) === 'singleplayer' && !c.level.toLowerCase().includes('pvp')
    );
    const campaignByDisplay: Record<string, { level: string; completion: number; quitRate: number; weight: number }> = {};
    for (const c of campaignFiltered) {
      const display = getLevelDisplayName(c.level);
      const w = Math.max(entriesByLevel[c.level] || 0, 1); // never zero so single-source rows are preserved
      const cur = campaignByDisplay[display];
      if (!cur) {
        campaignByDisplay[display] = {
          level: display,
          completion: c.completion,
          quitRate: c.quitRate,
          weight: w,
        };
      } else {
        const totalW = cur.weight + w;
        cur.completion = (cur.completion * cur.weight + c.completion * w) / totalW;
        cur.quitRate   = (cur.quitRate   * cur.weight + c.quitRate   * w) / totalW;
        cur.weight = totalW;
      }
    }
    const spCampaign = Object.values(campaignByDisplay).map(({ weight: _w, ...rest }) => rest);

    // ---- Merge levelStats by display name ----
    const statsFiltered = rv.levelStats.filter(s =>
      getLevelCategoryWithDefault(s.level) === 'singleplayer' && !s.level.toLowerCase().includes('pvp')
    );
    const statsByDisplay: Record<string, { level: string; entries: number; quitRate: number; avgTime: number }> = {};
    for (const s of statsFiltered) {
      const display = getLevelDisplayName(s.level);
      const cur = statsByDisplay[display];
      const e = s.entries || 0;
      if (!cur) {
        statsByDisplay[display] = {
          level: display,
          entries: e,
          quitRate: s.quitRate || 0,
          avgTime: s.avgTime || 0,
        };
      } else {
        const totalE = cur.entries + e;
        if (totalE > 0) {
          cur.quitRate = ((cur.quitRate * cur.entries) + ((s.quitRate || 0) * e)) / totalE;
          cur.avgTime  = ((cur.avgTime  * cur.entries) + ((s.avgTime  || 0) * e)) / totalE;
        }
        cur.entries = totalE;
      }
    }
    const spStats = Object.values(statsByDisplay);

    // ---- Sort by preferred Single Player order (matches against display name) ----
    const sortByPreferred = <T extends { level: string }>(arr: T[]) =>
      [...arr].sort((a, b) => {
        const ai = singlePlayerSortIndex(a.level);
        const bi = singlePlayerSortIndex(b.level);
        if (ai !== bi) return ai - bi;
        return a.level.localeCompare(b.level);
      });

    return { campaign: sortByPreferred(spCampaign), stats: sortByPreferred(spStats) };
  })();

  // Filtered data for Labz levels
  const labzData = (() => {
    if (!rv?.campaignProgress || !rv?.levelStats) return { campaign: [], stats: [] };

    const labzCampaign = rv.campaignProgress.filter(c =>
      getLevelCategoryWithDefault(c.level) === 'labz' && !c.level.toLowerCase().includes('hell') && !c.level.toLowerCase().includes('supertraversal')
    );
    const labzStats = rv.levelStats.filter(s =>
      getLevelCategoryWithDefault(s.level) === 'labz' && !s.level.toLowerCase().includes('supertraversal')
    );

    return { campaign: labzCampaign, stats: labzStats };
  })();

  // PVP & 2D levels — manually-categorized via Level Settings (no name-pattern
  // auto-detection), mirroring the Single Player / Labs filtering.
  const pvpData = (() => {
    if (!rv?.campaignProgress || !rv?.levelStats) return { campaign: [], stats: [] };
    return {
      campaign: rv.campaignProgress.filter(c => getLevelCategoryWithDefault(c.level) === 'pvp'),
      stats: rv.levelStats.filter(s => getLevelCategoryWithDefault(s.level) === 'pvp'),
    };
  })();

  const twoDData = (() => {
    if (!rv?.campaignProgress || !rv?.levelStats) return { campaign: [], stats: [] };
    return {
      campaign: rv.campaignProgress.filter(c => getLevelCategoryWithDefault(c.level) === 'twod'),
      stats: rv.levelStats.filter(s => getLevelCategoryWithDefault(s.level) === 'twod'),
    };
  })();

  // ----- Per-level economy (collectibles) ------------------------------------
  // Built from level_completed.items_collected (backend aggregates by raw
  // level_id). We merge by display-name + categorise via the same SP/Labs
  // resolution the rest of the dashboard uses, so a level reassigned in
  // Level Settings flows through identically.

  function buildEconomyForCategory(category: 'singleplayer' | 'labz'): EconomyRow[] {
    const itemsByLevel = rangedData?.levelItemsCollected;
    if (!itemsByLevel) return [];
    const merged: Record<string, EconomyRow> = {};
    for (const [levelId, currencies] of Object.entries(itemsByLevel)) {
      if (getLevelCategoryWithDefault(levelId) !== category) continue;
      // SP filter mirrors singlePlayerData: drop PvP. Labs mirrors labzData:
      // drop hell + supertraversal so duplicate "Icebergs"-style entries
      // don't appear under Labs after the SP override resolves them to SP.
      const lower = levelId.toLowerCase();
      if (category === 'singleplayer' && lower.includes('pvp')) continue;
      if (category === 'labz' && (lower.includes('hell') || lower.includes('supertraversal'))) continue;
      const display = getLevelDisplayName(levelId);
      const cur = merged[display] ?? { level: display, coin: 0, star: 0, orb: 0 };
      cur.coin += Number(currencies?.coin) || 0;
      cur.star += Number(currencies?.star) || 0;
      cur.orb  += Number(currencies?.orb)  || 0;
      merged[display] = cur;
    }
    return Object.values(merged);
  }

  const singlePlayerEconomyData = ((): EconomyRow[] => {
    const rows = buildEconomyForCategory('singleplayer');
    return [...rows].sort((a, b) => {
      const ai = singlePlayerSortIndex(a.level);
      const bi = singlePlayerSortIndex(b.level);
      if (ai !== bi) return ai - bi;
      return a.level.localeCompare(b.level);
    });
  // buildEconomyForCategory closes over data + getLevelCategoryWithDefault;
  // both are listed so the memo re-runs when the user reassigns a level.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })();

  const labzEconomyData = ((): EconomyRow[] => {
    return buildEconomyForCategory('labz');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })();

  // ----- Main menu button presses -------------------------------------------
  // Simple sort by count desc. Empty array -> section is hidden.
  const mainMenuButtonData = ((): Array<{ button: string; count: number }> => {
    const counts = rangedData?.mainMenuButtonCounts;
    if (!counts) return [];
    return Object.entries(counts)
      // Omit "LEVELS" — levels are shown segregated (3D / 2D / etc.) elsewhere.
      .filter(([button, c]) => (Number(c) || 0) > 0 && button.toLowerCase() !== 'levels')
      .map(([button, count]) => ({ button, count: Number(count) || 0 }))
      .sort((a, b) => b.count - a.count);
  })();

  // ----- Multiplier pickups (count per level_id, split by source) -----------
  // Two charts: one for 'triple_stomp', one for 'multiplier_box'. Each is a
  // bar chart of pickup count per unique level_id for that source.
  const multiplierBySource = ((): Record<string, Array<{ level: string; count: number }>> => {
    const counts = rangedData?.multiplierPickedCounts || {};
    const build = (src: string) => Object.entries(counts[src] || {})
      .filter(([, c]) => (Number(c) || 0) > 0)
      .map(([level, count]) => ({ level, count: Number(count) || 0 }))
      .sort((a, b) => b.count - a.count);
    return {
      triple_stomp: build('triple_stomp'),
      multiplier_box: build('multiplier_box'),
    };
  })();

  return {
    data, rangedData, rv, D, editorTop, singlePlayerData, labzData, pvpData, twoDData,
    singlePlayerEconomyData, labzEconomyData, mainMenuButtonData, multiplierBySource,
  };
}

type AnalyticsView = ReturnType<typeof buildAnalyticsView>;
/** Card renders run after the `!data` guard, so the view's data is non-null there. */
type CardView = AnalyticsView & { data: AnalyticsData; D: AnalyticsData };

export function AnalyticsDashboard() {
  const {
    data: rawData,
    selectedPlatform,
    timeRange,
    setTimeRange,
    isLoading,
    isFetching,
    error,
    refetch,
    clearCache,
    lastFetchTime,
    nextRefreshTime
  } = useAnalytics();
  
  const { isDarkMode } = useTheme();
  const { getLevelCategory, isSettingsOpen, openSettings, closeSettings } = useLevelSettings();
  const { inRange, isAllTime } = useDateRange();

  // ---- Country filter ----
  // One floating filter per tab (under the date range); every card follows it
  // unless the card's own selector (top-right of the card) overrides it.
  const [countries, setCountries] = useState<string[]>([]);
  const countryData = useAnalyticsCountries(rawData?.lastUpdated);
  const countryOpts = useMemo(() => countryOptions(countryData.payload, rawData), [countryData.payload, rawData]);
  const cardOverrides = useCardOverrides();

  // Filter levels by category using context + defaults. Anything not explicitly
  // assigned and not matched by a name pattern defaults to UGC (product default).
  const getLevelCategoryWithDefault = useCallback((levelId: string) => {
    const contextCategory = getLevelCategory(levelId);
    if (contextCategory) return contextCategory;
    return getDefaultLevelCategory(levelId) ?? 'ugc';
  }, [getLevelCategory]);

  const buildView = useCallback((selection: string[]) => buildAnalyticsView({
    rawData, payload: countryData.payload, ready: countryData.ready, selection,
    inRange, isAllTime, getLevelCategoryWithDefault,
  }), [rawData, countryData.payload, countryData.ready, inRange, isAllTime, getLevelCategoryWithDefault]);
  const viewFor = useViewCache(buildView);
  const { data } = viewFor(countries);
  /** Renders a card from the view for ITS countries (own override, else the tab's). */
  const CC = (id: string, render: (v: CardView) => ReactNode) => (
    <CountryCard id={id}>{selection => render(viewFor(selection) as CardView)}</CountryCard>
  );

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showEventCounts, setShowEventCounts] = useState(false);
  const [editorTimeScale, setEditorTimeScale] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const dashboardRef = useRef<HTMLDivElement>(null);

  // Time scale options for aggregation
  const TIME_SCALE_OPTIONS = [
    { value: 'all', label: 'All Time (Total)' },
    { value: 'day', label: 'Daily Average' },
    { value: 'week', label: 'Weekly Average' },
    { value: 'month', label: 'Monthly Average' },
  ];

  // Get all unique level IDs for settings modal
  const allLevelIds = useMemo(() => {
    if (!data?.levelStats) return [];
    return data.levelStats.map(s => s.level);
  }, [data?.levelStats]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await clearCache();
    setIsRefreshing(false);
  };

  // Export dashboard charts as ZIP
  const handleExportZip = useCallback(async () => {
    if (!dashboardRef.current || isExporting) return;
    
    setIsExporting(true);
    
    try {
      // Dynamic imports
      const html2canvasModule = await import('html2canvas');
      const html2canvas = html2canvasModule.default;
      const JSZip = (await import('jszip')).default;
      
      const zip = new JSZip();
      const timestamp = new Date().toISOString().split('T')[0];
      
      // Get all card sections
      const cards = dashboardRef.current.querySelectorAll('.rounded-xl');
      
      if (cards.length === 0) {
        alert('No charts found to export');
        setIsExporting(false);
        return;
      }
      
      // Process cards
      const totalCards = Math.min(cards.length, 15);
      
      for (let i = 0; i < totalCards; i++) {
        const card = cards[i] as HTMLElement;
        
        // Skip very small elements (likely not charts)
        if (card.offsetHeight < 100) continue;
        
        try {
          const canvas = await html2canvas(card, {
            backgroundColor: isDarkMode ? '#111827' : '#ffffff',
            scale: 2,
            useCORS: true,
            logging: false,
          });
          
          // Get card title for filename
          const titleEl = card.querySelector('h3');
          const cardTitle = titleEl?.textContent?.slice(0, 40).replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_') || `chart_${i + 1}`;
          
          // Convert canvas to blob
          const blob = await new Promise<Blob>((resolve) => {
            canvas.toBlob((b: Blob | null) => resolve(b!), 'image/png', 1.0);
          });
          
          // Add to zip
          zip.file(`${cardTitle}.png`, blob);
        } catch (err) {
          console.warn(`Failed to export card ${i}:`, err);
        }
      }
      
      // Generate and download zip
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `lokko_analytics_${selectedPlatform}_${timestamp}.zip`;
      link.click();
      URL.revokeObjectURL(link.href);
      
    } catch (err) {
      console.error('Failed to export ZIP:', err);
      alert('To enable ZIP export, run:\nnpm install html2canvas jszip');
    } finally {
      setIsExporting(false);
    }
  }, [selectedPlatform, isDarkMode, isExporting]);

  // Theme classes - improved light mode contrast
  const themeClasses = isDarkMode 
    ? 'bg-slate-950 text-gray-100' 
    : 'bg-gray-50 text-gray-900';
  
  const tooltipStyle = {
    backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
    border: `1px solid ${isDarkMode ? '#374151' : '#d1d5db'}`,
    borderRadius: '8px',
    padding: '8px 12px',
    color: isDarkMode ? '#f9fafb' : '#1f2937',
    boxShadow: isDarkMode ? 'none' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  };
  
  const gridColor = isDarkMode ? '#374151' : '#d1d5db';
  const textColor = isDarkMode ? '#9ca3af' : '#4b5563';

  // Show loading state with better messaging
  if (isLoading && !data) {
    return (
      <div className={`flex items-center justify-center py-20 ${themeClasses}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto" />
          <p className="mt-4 text-gray-400">
            {isFetching 
              ? 'Loading analytics data from database...' 
              : 'Loading analytics...'}
          </p>
          {isFetching && (
            <p className="mt-2 text-sm text-gray-500">
              This may take a minute on first load
            </p>
          )}
          {error && (
            <p className="mt-2 text-sm text-yellow-500">{error}</p>
          )}
        </div>
      </div>
    );
  }

  // Show fetching state (auto-retrying)
  if (isFetching && !data) {
    return (
      <div className={`flex items-center justify-center py-20 ${themeClasses}`}>
        <div className="text-center max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto" />
          <h3 className="text-lg font-semibold mt-4 mb-2">Loading Analytics Data</h3>
          <p className="text-gray-400 mb-2">{error || 'Fetching data from database...'}</p>
          <p className="text-sm text-gray-500">Auto-retrying every 5 seconds...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className={`flex items-center justify-center py-20 ${themeClasses}`}>
        <div className="text-center max-w-md">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Failed to Load Analytics</h3>
          <p className="text-gray-400 mb-4">{error}</p>
          <Button onClick={refetch}>Try Again</Button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  // Every card renders from its own view (see CC / buildAnalyticsView).

  // D1 retention hidden — using Sony DOMO data for retention instead

  return (
    <CountryScope value={{
      tabSelection: countries, options: countryOpts, isDark: isDarkMode, countLabel: 'players (all time)',
      disabled: !countryData.ready, disabledReason: countryData.message || 'Loading country data…',
      ...cardOverrides,
    }}>
    <>
      {/* Floating date-range filter: a horizontal card pinned to the top-left,
          just under the sticky Lokko Overseer header (~49px tall), so it stays
          visible while scrolling this long dashboard. Kept OUTSIDE dashboardRef
          so it never leaks into the PNG/ZIP export, which snapshots every
          .rounded-xl inside the ref. z-40 sits below the z-50 header/modals.
          Hidden below xl where it would overlap the charts. */}
      {/* top-[7.5rem] aligns the card top with the "Game Analytics" title row
          below (sticky header + p-6 padding), so it reads as a left-side peer of
          the right-side controls rather than floating loose above them. */}
      <div className="hidden xl:block fixed left-4 top-[7.5rem] z-40">
        <div
          className={`w-48 rounded-xl border shadow-lg backdrop-blur-sm p-3 ${
            isDarkMode ? 'bg-gray-900/90 border-gray-700' : 'bg-white/90 border-gray-200'
          }`}
        >
          <div
            className={`flex items-center gap-1.5 mb-2.5 text-[11px] font-semibold uppercase tracking-wider ${
              isDarkMode ? 'text-gray-500' : 'text-gray-400'
            }`}
          >
            <Calendar className="h-3.5 w-3.5" />
            Date Range
          </div>
          <DateRangeFilter orientation="vertical" />
          <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className={`flex items-center gap-1.5 mb-2 text-[11px] font-semibold uppercase tracking-wider ${
              isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <Globe className="h-3.5 w-3.5" />
              Country/Region
            </div>
            <CountryMultiSelect options={countryOpts} selected={countries} onChange={setCountries} align="left"
              isDark={isDarkMode} countLabel="players (all time)" compact
              disabled={!countryData.ready} disabledReason={countryData.message || 'Loading country data…'} />
            {countryData.ready && (
              <div className="mt-3">
                <CountryChips options={countryOpts} selected={countries} onChange={setCountries} isDark={isDarkMode} vertical max={8} />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={`min-h-screen ${themeClasses}`} ref={dashboardRef}>
      {/* xl:pl-56 reserves the gutter the fixed date-range card occupies (left-4
          + w-48 = 16..208px). Without it the card overlaps content between
          ~1280px and ~1710px, because `container mx-auto` only develops a wide
          enough left gutter above that. */}
      <div className="space-y-6 p-6 xl:pl-56">
        {/* Header. Top clearance is no longer needed now that xl:pl-56 reserves
            a left gutter for the fixed date-range card. */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Game Analytics</h1>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Last Updated: {lastFetchTime ? lastFetchTime.toLocaleString() : (data.lastUpdated ? new Date(data.lastUpdated).toLocaleString() : 'N/A')}
              {nextRefreshTime && (
                <span> · Next Update: {nextRefreshTime.toLocaleString()}</span>
              )}
            </p>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            {/* Platform selector hidden by request — Analytics always shows "overall" data */}
            {/* Date-range filter now floats as a left-side card (see below) */}

            {/* Below xl the floating card is hidden: same tab filter inline. */}
            <div className="xl:hidden">
              <CountryMultiSelect options={countryOpts} selected={countries} onChange={setCountries}
                isDark={isDarkMode} countLabel="players (all time)"
                disabled={!countryData.ready} disabledReason={countryData.message || 'Loading country data…'} />
            </div>

            {/* Time scale selector */}
            <div className="relative">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className={`appearance-none rounded-lg px-3 py-2 pr-7 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode 
                    ? 'bg-gray-800 border border-gray-700 text-gray-100' 
                    : 'bg-white border border-gray-300 text-gray-900'
                }`}
              >
                {TIME_SCALE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown className={`absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            </div>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleExportZip}
              disabled={isExporting}
              title="Export all charts as ZIP"
              className={`whitespace-nowrap ${isDarkMode ? '' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'}`}
            >
              {isExporting ? (
                <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-1" />
              )}
              <span className="hidden sm:inline">{isExporting ? 'Exporting...' : 'Export'}</span>
            </Button>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`whitespace-nowrap ${isDarkMode ? '' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'}`}
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline ml-1">Refresh</span>
            </Button>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={openSettings}
              className={`whitespace-nowrap ${isDarkMode ? '' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'}`}
            >
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline ml-1">Settings</span>
            </Button>
          </div>
        </div>

        {/* Level Settings Modal */}
        <LevelSettingsModal 
          isOpen={isSettingsOpen}
          onClose={closeSettings}
          allLevels={allLevelIds}
          isDark={isDarkMode}
        />

        {/* Avg. Editor Session Times - bar chart */}
        {CC('editor-times', v => (<>
        {(v.D.timeExpenditure?.levelEditor > 0 || v.D.timeExpenditure?.avatarEditor > 0 || v.D.timeExpenditure?.npcEditor > 0 || v.D.timeExpenditure?.editor2d > 0) && (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Timer className="h-4 w-4" />
                Avg. Editor Session Times
              </h3>
              <CardCountryButton />
            </div>
            <div className="p-4">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[
                    { editor: '3D Editor', avgTime: v.D.timeExpenditure.levelEditor || 0, samples: v.D.timeExpenditure.levelEditorSamples || 0 },
                    { editor: '2D Editor', avgTime: v.D.timeExpenditure.editor2d || 0, samples: v.D.timeExpenditure.editor2dSamples || 0 },
                    { editor: 'Avatar Editor', avgTime: v.D.timeExpenditure.avatarEditor || 0, samples: v.D.timeExpenditure.avatarEditorSamples || 0 },
                    { editor: 'NPC Editor', avgTime: v.D.timeExpenditure.npcEditor || 0, samples: v.D.timeExpenditure.npcEditorSamples || 0 },
                    { editor: 'Track Editor', avgTime: v.D.timeExpenditure.trackEditor || 0, samples: v.D.timeExpenditure.trackEditorSamples || 0 },
                    { editor: 'Chassis Editor', avgTime: v.D.timeExpenditure.chassisEditor || 0, samples: v.D.timeExpenditure.chassisEditorSamples || 0 },
                    { editor: 'Coop Editor', avgTime: v.D.timeExpenditure.coopLevelEditor || 0, samples: v.D.timeExpenditure.coopLevelEditorSamples || 0 },
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis
                      dataKey="editor"
                      tick={{ fontSize: 10, fill: textColor }}
                      stroke={gridColor}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: textColor }}
                      stroke={gridColor}
                      tickFormatter={(v) => `${v}m`}
                      domain={['auto', 'auto']}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value: number, _name: string, entry: any) => [
                        `${value?.toFixed(1) || 0} min (${entry?.payload?.samples || 0} samples)`,
                        'Avg Time'
                      ]}
                    />
                    <Bar
                      dataKey="avgTime"
                      fill="#3b82f6"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </ThemedCard>
        )}
        </>))}

        {/* Editor Gross Statistics — one card per editor arranged horizontally */}
        {CC('editor-gross', v => (<>
        {v.D.editorGrossStats && (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Database className="h-4 w-4" />
                Editor Gross Statistics
              </h3>
              <CardCountryButton />
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
                {/* 3D Editor */}
                <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-yellow-50 border border-yellow-100'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Layers className="h-4 w-4 text-yellow-400" />
                      <span className={`text-sm font-semibold ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>3D Editor</span>
                    </div>
                    <span title="Since Dec 18, 2025" className={`text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Since {daysSince('2025-12-18')} days</span>
                  </div>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Total Time</span><span className="font-medium">{v.D.editorGrossStats.levelEditorTotalTime || '0m'}</span></div>
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Times Opened</span><span className="font-medium">{formatCount(v.D.editorGrossStats.levelEditorOpens || 0)}</span></div>
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Level Saves</span><span className="font-medium">{formatCount(v.D.editorGrossStats.levelSaves || 0)}</span></div>
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Level Publishes</span><span className="font-medium">{formatCount(v.D.editorGrossStats.levelPublishes || 0)}</span></div>
                  </div>
                  <EditorTopCountries rows={v.editorTop?.level_editor} color="#facc15" isDark={isDarkMode} hasSaves={true} unavailable={countryData.ready ? null : countryData.message} />
                </div>
                {/* 2D Editor */}
                <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-blue-50 border border-blue-100'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Layers className="h-4 w-4 text-blue-400" />
                      <span className={`text-sm font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>2D Editor</span>
                    </div>
                    <span title="Since Mar 14, 2026" className={`text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Since {daysSince('2026-03-14')} days</span>
                  </div>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Total Time</span><span className="font-medium">{v.D.editorGrossStats.editor2dTotalTime || '0m'}</span></div>
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Times Opened</span><span className="font-medium">{formatCount(v.D.editorGrossStats.editor2dOpens || 0)}</span></div>
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Level Saves</span><span className="font-medium">{formatCount(v.D.editorGrossStats.level2dSaves || 0)}</span></div>
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Level Publishes</span><span className="font-medium">{formatCount(v.D.editorGrossStats.level2dPublishes || 0)}</span></div>
                  </div>
                  <EditorTopCountries rows={v.editorTop?.editor_2d} color="#60a5fa" isDark={isDarkMode} hasSaves={true} unavailable={countryData.ready ? null : countryData.message} />
                </div>
                {/* Avatar Editor */}
                <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-purple-50 border border-purple-100'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Palette className="h-4 w-4 text-purple-400" />
                      <span className={`text-sm font-semibold ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>Avatar Editor</span>
                    </div>
                    <span title="Since Dec 18, 2025" className={`text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Since {daysSince('2025-12-18')} days</span>
                  </div>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Total Time</span><span className="font-medium">{v.D.editorGrossStats.avatarEditorTotalTime || '0m'}</span></div>
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Times Opened</span><span className="font-medium">{formatCount(v.D.editorGrossStats.avatarEditorOpens || 0)}</span></div>
                  </div>
                  <EditorTopCountries rows={v.editorTop?.avatar_editor} color="#c084fc" isDark={isDarkMode} hasSaves={false} unavailable={countryData.ready ? null : countryData.message} />
                </div>
                {/* NPC Editor */}
                <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-green-50 border border-green-100'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Bot className="h-4 w-4 text-green-400" />
                      <span className={`text-sm font-semibold ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>NPC Editor</span>
                    </div>
                    <span title="Since Dec 18, 2025" className={`text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Since {daysSince('2025-12-18')} days</span>
                  </div>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Total Time</span><span className="font-medium">{v.D.editorGrossStats.npcEditorTotalTime || '0m'}</span></div>
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Times Opened</span><span className="font-medium">{formatCount(v.D.editorGrossStats.npcEditorOpens || 0)}</span></div>
                  </div>
                  <EditorTopCountries rows={v.editorTop?.npc_editor} color="#4ade80" isDark={isDarkMode} hasSaves={false} unavailable={countryData.ready ? null : countryData.message} />
                </div>
                {/* Track Editor */}
                <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-amber-50 border border-amber-100'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Layers className="h-4 w-4 text-amber-400" />
                      <span className={`text-sm font-semibold ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>Track Editor</span>
                    </div>
                    <span title="Since May 4, 2026" className={`text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Since {daysSince('2026-05-04')} days</span>
                  </div>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Total Time</span><span className="font-medium">{v.D.editorGrossStats.trackEditorTotalTime || '0m'}</span></div>
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Times Opened</span><span className="font-medium">{formatCount(v.D.editorGrossStats.trackEditorOpens || 0)}</span></div>
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Level Saves</span><span className="font-medium">{formatCount(v.D.editorGrossStats.trackEditorSaves || 0)}</span></div>
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Level Publishes</span><span className="font-medium">{formatCount(v.D.editorGrossStats.trackEditorPublishes || 0)}</span></div>
                  </div>
                  <EditorTopCountries rows={v.editorTop?.track_editor} color="#fbbf24" isDark={isDarkMode} hasSaves={true} unavailable={countryData.ready ? null : countryData.message} />
                </div>
                {/* Chassis Editor */}
                <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-cyan-50 border border-cyan-100'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Layers className="h-4 w-4 text-cyan-400" />
                      <span className={`text-sm font-semibold ${isDarkMode ? 'text-cyan-300' : 'text-cyan-700'}`}>Chassis Editor</span>
                    </div>
                    <span title="Since May 30, 2026" className={`text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Since {daysSince('2026-05-30')} days</span>
                  </div>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Total Time</span><span className="font-medium">{v.D.editorGrossStats.chassisEditorTotalTime || '0m'}</span></div>
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Times Opened</span><span className="font-medium">{formatCount(v.D.editorGrossStats.chassisEditorOpens || 0)}</span></div>
                  </div>
                  <EditorTopCountries rows={v.editorTop?.chassis_editor} color="#22d3ee" isDark={isDarkMode} hasSaves={false} unavailable={countryData.ready ? null : countryData.message} />
                </div>
                {/* Coop Level Editor */}
                <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-pink-50 border border-pink-100'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Layers className="h-4 w-4 text-pink-400" />
                      <span className={`text-sm font-semibold ${isDarkMode ? 'text-pink-300' : 'text-pink-700'}`}>Coop Level Editor</span>
                    </div>
                    <span title="Since May 30, 2026" className={`text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Since {daysSince('2026-05-30')} days</span>
                  </div>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Total Time</span><span className="font-medium">{v.D.editorGrossStats.coopLevelEditorTotalTime || '0m'}</span></div>
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Times Opened</span><span className="font-medium">{formatCount(v.D.editorGrossStats.coopLevelEditorOpens || 0)}</span></div>
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Level Saves</span><span className="font-medium">{formatCount(v.D.editorGrossStats.coopLevelEditorSaves || 0)}</span></div>
                    <div className="flex justify-between"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Level Publishes</span><span className="font-medium">{formatCount(v.D.editorGrossStats.coopLevelEditorPublishes || 0)}</span></div>
                  </div>
                  <EditorTopCountries rows={v.editorTop?.coop_level_editor} color="#f472b6" isDark={isDarkMode} hasSaves={true} unavailable={countryData.ready ? null : countryData.message} />
                </div>
              </div>
            </div>
          </ThemedCard>
        )}
        </>))}

        {/* Editor Sessions — time-series line chart with daily/weekly/monthly toggle */}
        {/* Editor Sessions — time-series with daily/weekly/monthly toggle */}
        {CC('editor-sessions', v => (<>
        <EditorSessionsChart data={v.data} editorTimeScale={editorTimeScale} setEditorTimeScale={setEditorTimeScale} isDarkMode={isDarkMode} gridColor={gridColor} textColor={textColor} tooltipStyle={tooltipStyle} />
        </>))}

        {/* Time Series Charts - Show when a time scale is selected */}
        {data.timeSeriesData && data.timeSeriesData.sessionTimeSeries && data.timeSeriesData.sessionTimeSeries.length > 0 && (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {timeRange === 'day' ? 'Daily' : timeRange === 'week' ? 'Weekly' : 'Monthly'} Session Time Trends
              </h3>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Average Session Time Over Time */}
                <div>
                  <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Average Session Time
                  </h4>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.timeSeriesData.sessionTimeSeries} margin={MARKER_CHART_MARGIN}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                        <XAxis 
                          dataKey="period"
                          tick={{ fontSize: 9, fill: textColor }}
                          stroke={gridColor}
                          tickFormatter={(v) => String(v).toUpperCase()}
                        />
                        <YAxis 
                          tick={{ fontSize: 12, fill: textColor }}
                          stroke={gridColor}
                          tickFormatter={(v) => `${v}m`}
                        />
                        <Tooltip 
                          contentStyle={tooltipStyle}
                          formatter={(value: number) => [
                            `${value?.toFixed(1) || 0} min`,
                            'Avg Session'
                          ]}
                          labelFormatter={(label) => `Period: ${label}`}
                        />
                        {/* ATH & Current markers */}
                        {data.timeSeriesData.sessionTimeSeries.length > 0 && (() => {
                          const vals = data.timeSeriesData.sessionTimeSeries.map((d: any) => d.avgSessionTime || 0);
                          const ath = Math.max(...vals);
                          const cur = vals[vals.length - 1];
                          return (<>
                            {ath > 0 && <ReferenceLine y={ath} stroke="#ef4444" strokeDasharray="4 3" strokeWidth={1.5}
                              label={markerLabel(`ATH ${ath.toFixed(1)}m`, '#ef4444', 'above')} />}
                            {cur > 0 && cur !== ath && <ReferenceLine y={cur} stroke="#3b82f6" strokeDasharray="4 3" strokeWidth={1.5}
                              label={markerLabel(`Now ${cur.toFixed(1)}m`, '#3b82f6', 'below')} />}
                          </>);
                        })()}
                        <Bar
                          dataKey="avgSessionTime"
                          fill="#10b981"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                {/* Sessions & Players Over Time */}
                <div>
                  <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Sessions & Players
                  </h4>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data.timeSeriesData.sessionTimeSeries} margin={MARKER_CHART_MARGIN}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                        <XAxis 
                          dataKey="period"
                          tick={{ fontSize: 9, fill: textColor }}
                          stroke={gridColor}
                          tickFormatter={(v) => String(v).toUpperCase()}
                        />
                        <YAxis 
                          tick={{ fontSize: 12, fill: textColor }}
                          stroke={gridColor}
                        />
                        <Tooltip 
                          contentStyle={tooltipStyle}
                          labelFormatter={(label) => `Period: ${label}`}
                        />
                        <Legend />
                        {/* ATH & Current markers for sessions */}
                        {data.timeSeriesData.sessionTimeSeries.length > 0 && (() => {
                          const vals = data.timeSeriesData.sessionTimeSeries.map((d: any) => d.sessionCount || 0);
                          const ath = Math.max(...vals);
                          const cur = vals[vals.length - 1];
                          return (<>
                            {ath > 0 && <ReferenceLine y={ath} stroke="#ef4444" strokeDasharray="4 3" strokeWidth={1.5}
                              label={markerLabel(`ATH ${ath.toLocaleString()}`, '#ef4444', 'above')} />}
                            {cur > 0 && cur !== ath && <ReferenceLine y={cur} stroke="#22c55e" strokeDasharray="4 3" strokeWidth={1.5}
                              label={markerLabel(`Now ${cur.toLocaleString()}`, '#22c55e', 'below')} />}
                          </>);
                        })()}
                        <Line
                          type="monotone"
                          dataKey="sessionCount"
                          name="Sessions"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          dot={{ fill: '#3b82f6', r: 4 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="playerCount"
                          name="Players"
                          stroke="#f59e0b"
                          strokeWidth={2}
                          dot={{ fill: '#f59e0b', r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </ThemedCard>
        )}

        {/* Global Time Distribution - where players spend time */}
        {CC('time', v => (<>
        {v.D.globalTimeDistribution && v.D.globalTimeDistribution.distribution.length > 0 && (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Where Players Spend Time (Avg per Session)
              </h3>
              <CardCountryButton />
            </div>
            <div className="p-4">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={v.D.globalTimeDistribution.distribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis 
                      dataKey="area"
                      tick={{ fontSize: 10, fill: textColor }}
                      stroke={gridColor}
                      tickFormatter={(v) => String(v).toUpperCase()}
                    />
                    <YAxis 
                      tick={{ fontSize: 12, fill: textColor }}
                      stroke={gridColor}
                      tickFormatter={(v) => `${v} min`}
                    />
                    <Tooltip 
                      contentStyle={tooltipStyle}
                      formatter={(value: number, _name: string, entry: any) => [
                        `${value?.toFixed(1) || 0} min (${entry?.payload?.sessions || 0} sessions)`,
                        'Avg Time'
                      ]}
                    />
                    <Bar 
                      dataKey="avgTime"
                      fill="#6366f1"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </ThemedCard>
        )}
        </>))}

        {/* Most Played Character */}
        {CC('characters', v => (<>
        <ThemedCard isDark={isDarkMode}>
          <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Users className="h-4 w-4" />
              Most Played Character {v.D.characterStats?.totalSelections ? `(${v.D.characterStats.totalSelections.toLocaleString()} total selections)` : ''}
            </h3>
            <CardCountryButton />
          </div>
          <div className="p-4">
            {v.D.characterStats?.characters && v.D.characterStats.characters.length > 0 ? (
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={v.D.characterStats.characters}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis
                      dataKey="character"
                      tick={{ fontSize: 12, fill: textColor }}
                      stroke={gridColor}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: textColor }}
                      stroke={gridColor}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value: number, _name: string, entry: any) => [
                        `${value.toLocaleString()} (${entry?.payload?.percentage || 0}%)`,
                        'Selections'
                      ]}
                    />
                    <Bar
                      dataKey="count"
                      fill="#f59e0b"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className={`text-center py-12 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                No character selection data yet. Data will appear once character_selected events are received.
              </div>
            )}
          </div>
        </ThemedCard>
        </>))}

        {/* Geo + Retention — removed, using Sony DOMO data instead */}

        {/* Single Player Levels Section */}
        {CC('sp-levels', v => (<>
        {v.singlePlayerData.campaign.length > 0 && (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Gamepad2 className="h-4 w-4 text-emerald-400" />
                Single Player Levels ({v.singlePlayerData.campaign.length} levels)
              </h3>
              <CardCountryButton />
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Completion Rate Chart */}
                <div>
                  <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Completion Rate
                  </h4>
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={v.singlePlayerData.campaign}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                        <XAxis 
                          dataKey="level" 
                          tick={({ x, y, payload }: any) => (<text x={x} y={y} dy={10} textAnchor="end" fill={textColor} fontSize={9} transform={`rotate(-35, ${x}, ${y})`}>{getLevelDisplayName(String(payload.value)).toUpperCase()}</text>)}
                          stroke={gridColor}
                          interval={0}
                          height={60}
                        />
                        <YAxis 
                          domain={[0, 100]} 
                          tick={{ fontSize: 12, fill: textColor }} 
                          stroke={gridColor}
                          tickFormatter={(v) => `${v}%`}
                        />
                        <Tooltip 
                          contentStyle={tooltipStyle}
                          formatter={(value: number) => [`${value.toFixed(1)}%`, 'Completion']}
                        />
                        <Bar 
                          dataKey="completion" 
                          fill="rgba(16, 185, 129, 0.8)"
                          stroke="#10b981"
                          strokeWidth={1}
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                {/* Average Session Time Chart */}
                <div>
                  <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Average Session Time
                  </h4>
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={v.singlePlayerData.stats.filter(s => s.avgTime)}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                        <XAxis 
                          dataKey="level" 
                          tick={({ x, y, payload }: any) => (<text x={x} y={y} dy={10} textAnchor="end" fill={textColor} fontSize={9} transform={`rotate(-35, ${x}, ${y})`}>{getLevelDisplayName(String(payload.value)).toUpperCase()}</text>)}
                          stroke={gridColor}
                          interval={0}
                          height={60}
                        />
                        <YAxis 
                          tick={{ fontSize: 12, fill: textColor }} 
                          stroke={gridColor}
                          tickFormatter={(v) => `${v}m`}
                        />
                        <Tooltip 
                          contentStyle={tooltipStyle}
                          formatter={(value: number) => [`${value.toFixed(1)} min`, 'Avg Time']}
                        />
                        <Bar 
                          dataKey="avgTime" 
                          fill="#3b82f6"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </ThemedCard>
        )}
        </>))}

        {/* Labs Levels Section */}
        {CC('labs-levels', v => (<>
        {v.labzData.campaign.length > 0 && (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <FlaskConical className="h-4 w-4 text-purple-400" />
                Labs Levels ({v.labzData.campaign.length} levels)
              </h3>
              <CardCountryButton />
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Completion Rate Chart */}
                <div>
                  <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Completion Rate
                  </h4>
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={v.labzData.campaign}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                        <XAxis 
                          dataKey="level" 
                          tick={({ x, y, payload }: any) => (<text x={x} y={y} dy={10} textAnchor="end" fill={textColor} fontSize={9} transform={`rotate(-35, ${x}, ${y})`}>{getLevelDisplayName(String(payload.value)).toUpperCase()}</text>)}
                          stroke={gridColor}
                          interval={0}
                          height={60}
                        />
                        <YAxis 
                          domain={[0, 100]} 
                          tick={{ fontSize: 12, fill: textColor }} 
                          stroke={gridColor}
                          tickFormatter={(v) => `${v}%`}
                        />
                        <Tooltip 
                          contentStyle={tooltipStyle}
                          formatter={(value: number) => [`${value.toFixed(1)}%`, 'Completion']}
                        />
                        <Bar 
                          dataKey="completion" 
                          fill="rgba(16, 185, 129, 0.8)"
                          stroke="#10b981"
                          strokeWidth={1}
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                {/* Average Session Time Chart */}
                <div>
                  <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Average Session Time
                  </h4>
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={v.labzData.stats.filter(s => s.avgTime)}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                        <XAxis 
                          dataKey="level" 
                          tick={({ x, y, payload }: any) => (<text x={x} y={y} dy={10} textAnchor="end" fill={textColor} fontSize={9} transform={`rotate(-35, ${x}, ${y})`}>{getLevelDisplayName(String(payload.value)).toUpperCase()}</text>)}
                          stroke={gridColor}
                          interval={0}
                          height={60}
                        />
                        <YAxis 
                          tick={{ fontSize: 12, fill: textColor }} 
                          stroke={gridColor}
                          tickFormatter={(v) => `${v}m`}
                        />
                        <Tooltip 
                          contentStyle={tooltipStyle}
                          formatter={(value: number) => [`${value.toFixed(1)} min`, 'Avg Time']}
                        />
                        <Bar 
                          dataKey="avgTime" 
                          fill="#3b82f6"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </ThemedCard>
        )}
        </>))}

        {/* PVP Levels Section — manually categorized via Level Settings */}
        {CC('pvp-levels', v => (<>
        {v.pvpData.campaign.length > 0 && (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Swords className="h-4 w-4 text-red-400" />
                PVP Levels ({v.pvpData.campaign.length} levels)
              </h3>
              <CardCountryButton />
            </div>
            <div className="p-4">
              <div className={`grid grid-cols-1 ${SHOW_PVP_COMPLETION ? 'lg:grid-cols-2' : ''} gap-6`}>
                {SHOW_PVP_COMPLETION && (
                <div>
                  <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Completion Rate</h4>
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={v.pvpData.campaign}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                        <XAxis dataKey="level" tick={({ x, y, payload }: any) => (<text x={x} y={y} dy={10} textAnchor="end" fill={textColor} fontSize={9} transform={`rotate(-35, ${x}, ${y})`}>{getLevelDisplayName(String(payload.value)).toUpperCase()}</text>)} stroke={gridColor} interval={0} height={60} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: textColor }} stroke={gridColor} tickFormatter={(v) => `${v}%`} />
                        <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [`${value.toFixed(1)}%`, 'Completion']} />
                        <Bar dataKey="completion" fill="rgba(239, 68, 68, 0.8)" stroke="#ef4444" strokeWidth={1} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                )}
                <div>
                  <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Average Session Time</h4>
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={v.pvpData.stats.filter(s => s.avgTime)}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                        <XAxis dataKey="level" tick={({ x, y, payload }: any) => (<text x={x} y={y} dy={10} textAnchor="end" fill={textColor} fontSize={9} transform={`rotate(-35, ${x}, ${y})`}>{getLevelDisplayName(String(payload.value)).toUpperCase()}</text>)} stroke={gridColor} interval={0} height={60} />
                        <YAxis tick={{ fontSize: 12, fill: textColor }} stroke={gridColor} tickFormatter={(v) => `${v}m`} />
                        <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [`${value.toFixed(1)} min`, 'Avg Time']} />
                        <Bar dataKey="avgTime" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </ThemedCard>
        )}
        </>))}

        {/* 2D Levels Section — manually categorized via Level Settings */}
        {CC('twod-levels', v => (<>
        {v.twoDData.campaign.length > 0 && (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Square className="h-4 w-4 text-amber-400" />
                2D Levels ({v.twoDData.campaign.length} levels)
              </h3>
              <CardCountryButton />
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Completion Rate</h4>
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={v.twoDData.campaign}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                        <XAxis dataKey="level" tick={({ x, y, payload }: any) => (<text x={x} y={y} dy={10} textAnchor="end" fill={textColor} fontSize={9} transform={`rotate(-35, ${x}, ${y})`}>{getLevelDisplayName(String(payload.value)).toUpperCase()}</text>)} stroke={gridColor} interval={0} height={60} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: textColor }} stroke={gridColor} tickFormatter={(v) => `${v}%`} />
                        <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [`${value.toFixed(1)}%`, 'Completion']} />
                        <Bar dataKey="completion" fill="rgba(245, 158, 11, 0.8)" stroke="#f59e0b" strokeWidth={1} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div>
                  <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Average Session Time</h4>
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={v.twoDData.stats.filter(s => s.avgTime)}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                        <XAxis dataKey="level" tick={({ x, y, payload }: any) => (<text x={x} y={y} dy={10} textAnchor="end" fill={textColor} fontSize={9} transform={`rotate(-35, ${x}, ${y})`}>{getLevelDisplayName(String(payload.value)).toUpperCase()}</text>)} stroke={gridColor} interval={0} height={60} />
                        <YAxis tick={{ fontSize: 12, fill: textColor }} stroke={gridColor} tickFormatter={(v) => `${v}m`} />
                        <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [`${value.toFixed(1)} min`, 'Avg Time']} />
                        <Bar dataKey="avgTime" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </ThemedCard>
        )}
        </>))}

        {/* Single Player Economy (collectibles from level_completed.items_collected) */}
        {CC('sp-economy', v => (<>
        {v.singlePlayerEconomyData.length > 0 && (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Coins className="h-4 w-4 text-amber-400" />
                Single Player Economy ({v.singlePlayerEconomyData.length} levels)
              </h3>
              <CardCountryButton />
            </div>
            <div className="p-4">
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={v.singlePlayerEconomyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis
                      dataKey="level"
                      tick={({ x, y, payload }: any) => (<text x={x} y={y} dy={10} textAnchor="end" fill={textColor} fontSize={9} transform={`rotate(-35, ${x}, ${y})`}>{String(payload.value).toUpperCase()}</text>)}
                      stroke={gridColor}
                      interval={0}
                      height={70}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: textColor }}
                      stroke={gridColor}
                      tickFormatter={formatAxisCount}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                    />
                    <Legend />
                    <Bar dataKey="coin" name="Coin" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="star" name="Star" fill="#eab308" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="orb"  name="Orb"  fill="#a855f7" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </ThemedCard>
        )}
        </>))}

        {/* Labs Economy (collectibles from level_completed.items_collected) */}
        {CC('labs-economy', v => (<>
        {v.labzEconomyData.length > 0 && (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Coins className="h-4 w-4 text-purple-400" />
                Labs Economy ({v.labzEconomyData.length} levels)
              </h3>
              <CardCountryButton />
            </div>
            <div className="p-4">
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={v.labzEconomyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis
                      dataKey="level"
                      tick={({ x, y, payload }: any) => (<text x={x} y={y} dy={10} textAnchor="end" fill={textColor} fontSize={9} transform={`rotate(-35, ${x}, ${y})`}>{String(payload.value).toUpperCase()}</text>)}
                      stroke={gridColor}
                      interval={0}
                      height={70}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: textColor }}
                      stroke={gridColor}
                      tickFormatter={formatAxisCount}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                    />
                    <Legend />
                    <Bar dataKey="coin" name="Coin" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="star" name="Star" fill="#eab308" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="orb"  name="Orb"  fill="#a855f7" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </ThemedCard>
        )}
        </>))}

        {/* Main Menu Button Presses (from main_menu_button_pressed event) */}
        {CC('main-menu', v => (<>
        {v.mainMenuButtonData.length > 0 && (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <MousePointerClick className="h-4 w-4 text-blue-400" />
                Main Menu Button Presses ({v.mainMenuButtonData.length} buttons)
              </h3>
              <CardCountryButton />
            </div>
            <div className="p-4">
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={v.mainMenuButtonData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis
                      dataKey="button"
                      tick={({ x, y, payload }: any) => (<text x={x} y={y} dy={10} textAnchor="end" fill={textColor} fontSize={9} transform={`rotate(-35, ${x}, ${y})`}>{String(payload.value).toUpperCase()}</text>)}
                      stroke={gridColor}
                      interval={0}
                      height={70}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: textColor }}
                      stroke={gridColor}
                      tickFormatter={formatAxisCount}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value: number) => [value.toLocaleString(), 'Presses']}
                    />
                    <Bar dataKey="count" name="Presses" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </ThemedCard>
        )}
        </>))}

        {/* Friend Requests — stacked bar (Sent / Accepted / Rejected), with combine toggle */}
        {CC('friend-requests', v => (<>
        <FriendRequestsChart data={v.data} isDarkMode={isDarkMode} gridColor={gridColor} textColor={textColor} tooltipStyle={tooltipStyle} />
        </>))}

        {/* Multiplier Pickups — per-level count, one chart per source */}
        {CC('multipliers', v => (<>
        {([
          { source: 'triple_stomp', label: 'Triple Stomp', color: '#f59e0b' },
          { source: 'multiplier_box', label: 'Multiplier Box', color: '#a855f7' },
        ] as const).map(({ source, label, color }) => {
          const rows = v.multiplierBySource[source] || [];
          if (rows.length === 0) return null;
          return (
            <ThemedCard key={source} isDark={isDarkMode}>
              <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Sparkles className="h-4 w-4" style={{ color }} />
                  Multiplier Pickups — {label} ({rows.length} levels)
                </h3>
                <CardCountryButton />
              </div>
              <div className="p-4">
                <div className="max-h-[420px] overflow-y-auto">
                  <div style={{ height: Math.max(320, rows.length * 28) }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={rows} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                        <XAxis
                          type="number"
                          tick={{ fontSize: 12, fill: textColor }}
                          stroke={gridColor}
                          tickFormatter={formatAxisCount}
                        />
                        <YAxis
                          type="category"
                          dataKey="level"
                          tick={{ fontSize: 9, fill: textColor }}
                          stroke={gridColor}
                          width={140}
                          interval={0}
                          tickFormatter={(v) => getLevelDisplayName(String(v))}
                        />
                        <Tooltip
                          contentStyle={tooltipStyle}
                          formatter={(value: number) => [value.toLocaleString(), 'Pickups']}
                          labelFormatter={(l) => getLevelDisplayName(String(l))}
                        />
                        <Bar dataKey="count" name="Pickups" fill={color} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </ThemedCard>
          );
        })}
        </>))}

        {/* Daily Session Length — removed */}

        {/* 3D Editor Items Usage */}
        {CC('blocks-3d', v => (<>
        <ThemedCard isDark={isDarkMode}>
          <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Blocks className="h-4 w-4" />
              3D Editor Items Usage {v.data.topEditorBlocks?.totalPlacements ? `(${v.data.topEditorBlocks.totalPlacements.toLocaleString()} total placements)` : ''}
            </h3>
            <CardCountryButton />
          </div>
          <div className="p-4">
            {v.data.topEditorBlocks?.blocks && v.data.topEditorBlocks.blocks.length > 0 ? (
              <div className="max-h-[500px] overflow-y-auto">
                <div style={{ height: Math.max(320, v.data.topEditorBlocks.blocks.length * 30) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={v.data.topEditorBlocks.blocks.map((b: any) => ({ ...b, block: resolveBlockName(b.block) }))} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 12, fill: textColor }}
                        stroke={gridColor}
                      />
                      <YAxis
                        type="category"
                        dataKey="block"
                        width={180}
                        tick={{ fontSize: 11, fill: textColor }}
                        stroke={gridColor}
                      />
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={(value: number, _name: string, entry: any) => [
                          `${value.toLocaleString()} (${entry?.payload?.percentage || 0}%)`,
                          'Placements'
                        ]}
                      />
                      <Bar
                        dataKey="count"
                        fill="#8b5cf6"
                        radius={[0, 4, 4, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <div className={`text-center py-12 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                No block placement data yet. Data will appear once level_editor_block_placed events are received.
              </div>
            )}
          </div>
        </ThemedCard>
        </>))}

        {/* 2D Editor Items Usage */}
        {CC('blocks-2d', v => (<>
        {v.data.topEditor2dBlocks?.blocks && v.data.topEditor2dBlocks.blocks.length > 0 && (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Blocks className="h-4 w-4" />
                2D Editor Items Usage {v.data.topEditor2dBlocks?.totalPlacements ? `(${v.data.topEditor2dBlocks.totalPlacements.toLocaleString()} total placements)` : ''}
              </h3>
              <CardCountryButton />
            </div>
            <div className="p-4">
              <div className="max-h-[500px] overflow-y-auto">
                <div style={{ height: Math.max(320, v.data.topEditor2dBlocks.blocks.length * 30) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={v.data.topEditor2dBlocks.blocks.map((b: any) => ({ ...b, block: resolveBlockName(b.block, true) }))} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 12, fill: textColor }}
                        stroke={gridColor}
                      />
                      <YAxis
                        type="category"
                        dataKey="block"
                        width={140}
                        tick={{ fontSize: 11, fill: textColor }}
                        stroke={gridColor}
                      />
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={(value: number, _name: string, entry: any) => [
                          `${value.toLocaleString()} (${entry?.payload?.percentage || 0}%)`,
                          'Placements'
                        ]}
                      />
                      <Bar
                        dataKey="count"
                        fill="#06b6d4"
                        radius={[0, 4, 4, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </ThemedCard>
        )}
        </>))}

        {/* Contest Stats - only show if data exists */}
        {CC('contest', v => (<>
        {v.data.contestStats && v.data.contestStats.total > 0 && (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Trophy className="h-4 w-4" />
                Contest Stats ({v.data.contestStats.total} total interactions)
              </h3>
              <CardCountryButton />
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard
                  icon={<Trophy className="h-4 w-4 text-green-400" />}
                  label="Register Tapped"
                  value={formatCount(v.data.contestStats.totalRegistered)}
                  isDark={isDarkMode}
                />
                <StatCard
                  icon={<Trophy className="h-4 w-4 text-red-400" />}
                  label="Skip Tapped"
                  value={formatCount(v.data.contestStats.totalSkipped)}
                  isDark={isDarkMode}
                />
                <StatCard
                  icon={<Trophy className="h-4 w-4 text-blue-400" />}
                  label="Skip Rate"
                  value={`${v.data.contestStats.skipRate}%`}
                  isDark={isDarkMode}
                />
              </div>
            </div>
          </ThemedCard>
        )}
        </>))}

        {/* Cinematic Stats - only show if data exists */}
        {CC('cinematics', v => (<>
        {v.data.cinematicStats && v.data.cinematicStats.totalViews > 0 && (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Film className="h-4 w-4" />
                Cinematic Stats ({v.data.cinematicStats.totalViews} total views)
              </h3>
              <CardCountryButton />
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard
                  icon={<Film className="h-4 w-4 text-green-400" />}
                  label="Completed"
                  value={formatCount(v.data.cinematicStats.totalCompleted)}
                  isDark={isDarkMode}
                />
                <StatCard
                  icon={<Film className="h-4 w-4 text-red-400" />}
                  label="Skipped"
                  value={formatCount(v.data.cinematicStats.totalSkipped)}
                  isDark={isDarkMode}
                />
                <StatCard
                  icon={<Film className="h-4 w-4 text-blue-400" />}
                  label="Skip Rate"
                  value={`${v.data.cinematicStats.skipRate}%`}
                  isDark={isDarkMode}
                />
              </div>
            </div>
          </ThemedCard>
        )}
        </>))}

        {/* Feedback Stats - only show if data exists */}
        {CC('feedback', v => (<>
        {v.data.feedbackStats && v.data.feedbackStats.initiated > 0 && (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Feedback Stats
              </h3>
              <CardCountryButton />
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard
                  icon={<MessageSquare className="h-4 w-4 text-blue-400" />}
                  label="Initiated"
                  value={formatCount(v.data.feedbackStats.initiated)}
                  isDark={isDarkMode}
                />
                <StatCard
                  icon={<MessageSquare className="h-4 w-4 text-green-400" />}
                  label="Submitted"
                  value={formatCount(v.data.feedbackStats.submitted)}
                  isDark={isDarkMode}
                />
                <StatCard
                  icon={<MessageSquare className="h-4 w-4 text-purple-400" />}
                  label="Submission Rate"
                  value={`${v.data.feedbackStats.submissionRate}%`}
                  isDark={isDarkMode}
                />
              </div>
            </div>
          </ThemedCard>
        )}
        </>))}

        {/* Level Editor Onboarding - only show if data exists */}
        {CC('le-onboarding', v => (<>
        {v.data.levelEditorOnboarding && v.data.levelEditorOnboarding.started > 0 && (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Layers className="h-4 w-4" />
                Level Editor Onboarding
              </h3>
              <CardCountryButton />
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard
                  icon={<Layers className="h-4 w-4 text-blue-400" />}
                  label="Started"
                  value={formatCount(v.data.levelEditorOnboarding.started)}
                  isDark={isDarkMode}
                />
                <StatCard
                  icon={<Layers className="h-4 w-4 text-green-400" />}
                  label="Completed"
                  value={formatCount(v.data.levelEditorOnboarding.completed)}
                  isDark={isDarkMode}
                />
                <StatCard
                  icon={<Layers className="h-4 w-4 text-purple-400" />}
                  label="Completion Rate"
                  value={`${v.data.levelEditorOnboarding.completionRate}%`}
                  isDark={isDarkMode}
                />
              </div>
            </div>
          </ThemedCard>
        )}
        </>))}

        {/* Onboarding Funnel */}
        {CC('onboarding', v => (<>
        {v.data.onboardingFunnel.length > 0 && (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Onboarding Funnel (SP Checkpoints)
              </h3>
              <CardCountryButton />
            </div>
            <div className="p-4">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={v.data.onboardingFunnel}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis 
                      dataKey="checkpoint" 
                      tick={{ fontSize: 10, fill: textColor }} 
                      stroke={gridColor}
                      
                      
                      height={80}
                    />
                    <YAxis 
                      tick={{ fontSize: 12, fill: textColor }} 
                      stroke={gridColor}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <Tooltip 
                      contentStyle={tooltipStyle}
                      formatter={(value: number, _name: string, entry: any) => [
                        `${value?.toFixed(1) || 0}% (${formatCount(entry?.payload?.players || 0)} players)`,
                        'Reached'
                      ]}
                    />
                    <Bar dataKey="percentage" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </ThemedCard>
        )}
        </>))}

        {/* Player Churn Points */}
        {CC('churn', v => (<>
        {v.data.churnPoints.length > 0 && (
          <ThemedCard isDark={isDarkMode}>
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Probable Player Churn Points
              </h3>
              <CardCountryButton />
            </div>
            <div className="p-4">
              <div className="h-[450px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={v.data.churnPoints} 
                    layout="vertical" 
                    margin={{ left: 10, right: 30, top: 10, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} horizontal={true} vertical={false} />
                    <XAxis type="number" tick={{ fontSize: 12, fill: textColor }} stroke={gridColor} />
                    <YAxis 
                      type="category" 
                      dataKey="category" 
                      width={200}
                      tick={{ fontSize: 11, fill: isDarkMode ? '#f3f4f6' : '#374151' }}
                      stroke="transparent"
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip 
                      contentStyle={tooltipStyle}
                      formatter={(value: number) => [formatCount(value), 'Players']}
                    />
                    <Bar 
                      dataKey="count" 
                      fill="#f59e0b" 
                      radius={[0, 4, 4, 0]}
                      barSize={24}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className={`text-xs mt-3 p-3 rounded-lg ${isDarkMode ? 'bg-amber-900/20 border border-amber-700/30' : 'bg-amber-50 border border-amber-200'}`}>
                <p className={`font-medium mb-1 ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                  ⚠️ Important: This is indicative data, not definitive churn analysis
                </p>
                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                  This chart shows each player's <strong>last recorded event</strong> in our analytics database. 
                  A player appearing here doesn't necessarily mean they've quit the game permanently — they may 
                  still be active but haven't triggered a tracked event recently, or their session may have ended 
                  normally. Use this as a directional indicator to identify potential friction points, not as 
                  absolute churn v.data.
                </p>
              </div>
            </div>
          </ThemedCard>
        )}
        </>))}

        {/* All Levels Search Section */}
        {CC('level-search', v => (<>
        {v.data.levelStats.length > 0 && (
          <LevelSearch
            levelStats={v.D.levelStats}
            campaignProgress={v.D.campaignProgress}
            isDark={isDarkMode}
          />
        )}
        </>))}

        {/* Event Counts Dropdown */}
        {data.eventCounts && (
          <ThemedCard isDark={isDarkMode}>
            <div 
              className="p-4 cursor-pointer flex items-center justify-between"
              onClick={() => setShowEventCounts(!showEventCounts)}
            >
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Database className="h-4 w-4" />
                Raw Event Counts (Total: {formatCount(data.eventCounts.total || 0)})
              </h3>
              {showEventCounts ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
            {showEventCounts && (
              <div className="p-4 border-t border-gray-700/50">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {Object.entries(data.eventCounts.byType || {})
                    .sort((a, b) => (b[1] as number) - (a[1] as number))
                    .map(([eventName, count]) => (
                      <div 
                        key={eventName} 
                        className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-100'}`}
                      >
                        <p className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} title={eventName}>
                          {eventName}
                        </p>
                        <p className="font-bold">{formatCount(count as number)}</p>
                      </div>
                    ))
                  }
                </div>
              </div>
            )}
          </ThemedCard>
        )}
      </div>
      </div>
    </>
    </CountryScope>
  );
}
